import { haversineMeters } from '@traintopia/domain'
import type { VehiclePosition } from '../store/transit'

export interface RiderLocation {
  latitude: number
  longitude: number
  accuracy?: number
}

export type VehicleProximity = 'near' | 'far' | 'uncertain'

export interface ArrivalDetection {
  confirmedTogether: boolean
  shouldExit: boolean
}

/**
 * Boarding already uses a generous 400 m radius because underground GPS and realtime
 * vehicle positions are both noisy. Arrival tracking uses the same radius to establish
 * that rider and vehicle are together, then a wider radius to prove that they separated.
 * The gap is deliberate hysteresis: fixes near the boundary must not repeatedly board and
 * unboard the rider.
 */
export const ONBOARD_RADIUS_M = 400
export const EXIT_RADIUS_M = 600

export function distanceMeters(
  a: Pick<RiderLocation, 'latitude' | 'longitude'>,
  b: Pick<RiderLocation, 'latitude' | 'longitude'>,
): number {
  return haversineMeters(a, b)
}

/**
 * A missing or boundary fix is intentionally inconclusive. We only report `far` when the
 * measured separation clears the exit radius plus the phone's stated uncertainty, so an
 * unavailable/poor fix can never eject the rider from a vehicle by itself.
 */
export function vehicleProximity(
  location: RiderLocation | null,
  vehicle: Pick<VehiclePosition, 'latitude' | 'longitude'> | null,
): VehicleProximity {
  if (!location || !vehicle) return 'uncertain'
  const accuracy = Number.isFinite(location.accuracy) ? Math.max(0, location.accuracy ?? 0) : 0
  const distance = distanceMeters(location, vehicle)
  if (distance <= ONBOARD_RADIUS_M + accuracy) return 'near'
  if (distance >= EXIT_RADIUS_M + accuracy) return 'far'
  return 'uncertain'
}

/** Require a positive onboard observation before a later far observation can end a ride. */
export function updateArrivalDetection(
  confirmedTogether: boolean,
  proximity: VehicleProximity,
): ArrivalDetection {
  if (proximity === 'near') return { confirmedTogether: true, shouldExit: false }
  if (proximity === 'far' && confirmedTogether) {
    return { confirmedTogether: false, shouldExit: true }
  }
  return { confirmedTogether, shouldExit: false }
}
